<!--
  BarChart — grouped bars (income vs. spending, or spending by category) with
  an optional line overlay for a running total, e.g. net.

  Geometry only comes from src/lib/charts: `niceTicks` picks the gridlines,
  `bandPositions` slots the groups across the plot width and, a second time,
  slots the bars within a group's slot. This component turns those numbers
  into `<rect>`/`<line>` attributes and nothing more — every figure a person
  reads is the caller's `display` string, verbatim.

  The gridlines are the one place a tick is a bare number with no
  caller-supplied `display`, and this component is still not allowed to invent
  formatting for it — that is exactly the inconsistency the no-formatting rule
  exists to prevent (see the header comment on numbers.ts). So the caller
  passes `tickLabel`, a formatter of its own, and the axis is labelled with
  whatever that returns. Without it the gridlines draw unlabelled, purely as
  scale.

  Pass it. An unlabelled axis is a chart you cannot read a magnitude off: the
  bars have relative heights and nothing to measure them against, and the only
  figures left are the bar's `display` on hover and the visually-hidden table —
  neither of which a person scanning the screen sees. The prop is optional
  because the geometry is honest without it, not because the chart is finished
  without it.

  Colour follows the same rule as DonutChart: a bar's colour comes from its
  series' position in the `series` prop, so a category keeps its colour
  between this chart and the donut as long as the screen hands both the same
  ordered list.

  Net (income minus spending) is the one figure this chart can show negative,
  and a losing month is the whole reason the chart exists — so the y-domain
  is extended below zero to fit the most negative `line` value rather than
  clamping or letting it draw past the plot edge. `lineDisplay` is the line's
  own already-formatted string for the visually-hidden table; `line` itself
  is a bare number with nowhere safe to print it as text (same reasoning as
  the gridlines above).
-->
<script lang="ts">
  import { bandPositions, linearScale } from '../../../lib/charts/scale';
  import { niceTicks } from '../../../lib/charts/ticks';

  interface Bar {
    series: string;
    value: number;
    /** Already formatted by the caller — this component never rounds. */
    display: string;
  }

  interface Group {
    key: string;
    label: string;
    bars: Bar[];
    /** The net-line value for this group, if the chart carries one. */
    line?: number;
    /** `line`, already formatted by the caller — shown only in the text alternative. */
    lineDisplay?: string;
  }

  interface Props {
    groups: readonly Group[];
    series: readonly { key: string; label: string }[];
    caption: string;
    /** What the overlay line means, e.g. "Net". Also what turns it on. */
    lineLabel?: string;
    /**
     * The caller's formatter for an axis tick — this component never formats a
     * number itself. Supplying it labels the gridlines and reserves the width
     * to do so; omitting it leaves them as bare scale.
     */
    tickLabel?: (value: number) => string;
  }

  const { groups, series, caption, lineLabel, tickLabel }: Props = $props();

  const RAMP_SIZE = 8;
  const WIDTH = 560;
  const HEIGHT = 280;
  const MARGIN = { top: 16, right: 8, bottom: 32 };
  /** Room for the tick labels, only when there are tick labels to make room for. */
  const AXIS_WIDTH = 48;
  const marginLeft = $derived(tickLabel ? AXIS_WIDTH : 8);
  const PLOT_WIDTH = $derived(WIDTH - marginLeft - MARGIN.right);
  const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

  const hasLine = $derived(lineLabel !== undefined && groups.some((g) => Number.isFinite(g.line)));

  const domainMax = $derived.by(() => {
    let max = 0;
    for (const group of groups) {
      for (const bar of group.bars) {
        if (Number.isFinite(bar.value) && bar.value > max) max = bar.value;
      }
      if (Number.isFinite(group.line) && (group.line as number) > max) max = group.line as number;
    }
    return max;
  });

  const ticks = $derived(niceTicks(domainMax));
  /** The top gridline is the axis ceiling, so the scale maps to it exactly. */
  const axisMax = $derived(ticks[ticks.length - 1] ?? 0);

  /**
   * Net can go negative — a losing month is the single most important thing
   * this chart exists to show — but `niceTicks`/`linearScale` only cover a
   * 0-up domain. So the axis floor is extended downward using the exact
   * step `niceTicks` already chose (the gap between its first two ticks),
   * far enough to fit the most negative `line` value, and `linearScale` is
   * then asked to scale that whole extended span (0..`axisMax - domainFloor`)
   * rather than just the positive part. `yOf` shifts every value up by
   * `-domainFloor` before handing it to that scale, then flips the result
   * from "height above the floor" to an SVG y-coordinate. When nothing is
   * negative, `domainFloor` is 0 and this reduces exactly to the old
   * 0-based mapping.
   */
  const domainMin = $derived.by(() => {
    let min = 0;
    for (const group of groups) {
      if (Number.isFinite(group.line) && (group.line as number) < min) min = group.line as number;
    }
    return min;
  });
  const tickStep = $derived(ticks.length > 1 ? ticks[1] - ticks[0] : axisMax || 1);
  const domainFloor = $derived(domainMin < 0 ? -Math.ceil(Math.abs(domainMin) / tickStep) * tickStep : 0);
  const domainSpan = $derived(axisMax - domainFloor);
  const shiftedScale = $derived(linearScale(domainSpan, PLOT_HEIGHT));

  function yOf(value: number): number {
    const shifted = (Number.isFinite(value) ? value : 0) - domainFloor;
    return MARGIN.top + PLOT_HEIGHT - shiftedScale(shifted);
  }

  /**
   * The gridlines actually drawn: `niceTicks` only covers 0..max, so when the
   * floor has been pushed below zero to fit a negative net, the space the line
   * dives into would otherwise carry no gridline and no label — a figure with
   * nothing to measure it against, which is the whole thing an axis is for.
   * The same `tickStep` continues downward, counted in whole steps rather than
   * accumulated, so the negative ticks land on exact multiples.
   */
  const axisTicks = $derived.by(() => {
    const below: number[] = [];
    const steps = tickStep > 0 ? Math.round(Math.abs(domainFloor) / tickStep) : 0;
    for (let i = steps; i > 0; i--) below.push(-i * tickStep);
    return [...below, ...ticks];
  });

  const groupBands = $derived(bandPositions(groups.length, PLOT_WIDTH, 0.18));

  interface PlacedBar extends Bar {
    x: number;
    width: number;
    y: number;
    height: number;
    colorIndex: number;
  }

  interface PlacedGroup {
    group: Group;
    bars: PlacedBar[];
    /** Centre of this group's band, for the x-axis label. */
    labelX: number;
    linePoint: { x: number; y: number } | null;
  }

  const placed = $derived.by((): PlacedGroup[] => {
    const baseline = yOf(0);
    return groups.map((group, gi) => {
      const band = groupBands[gi] ?? { x: 0, width: 0 };
      const bandX = marginLeft + band.x;
      const subBands = bandPositions(series.length, band.width, 0.12);
      const bars: PlacedBar[] = series
        .map((se, si) => {
          const bar = group.bars.find((b) => b.series === se.key);
          if (!bar) return null;
          const sub = subBands[si] ?? { x: 0, width: 0 };
          const value = Number.isFinite(bar.value) && bar.value > 0 ? bar.value : 0;
          const top = yOf(value);
          const height = Math.max(0, baseline - top);
          return {
            ...bar,
            x: bandX + sub.x,
            width: sub.width,
            y: top,
            height,
            colorIndex: si % RAMP_SIZE
          };
        })
        .filter((b): b is PlacedBar => b !== null);

      const linePoint =
        hasLine && Number.isFinite(group.line)
          ? { x: bandX + band.width / 2, y: yOf(group.line as number) }
          : null;

      return { group, bars, labelX: bandX + band.width / 2, linePoint };
    });
  });

  const linePoints = $derived(placed.map((p) => p.linePoint).filter((p): p is { x: number; y: number } => p !== null));
  const linePath = $derived(linePoints.map((p) => `${p.x},${p.y}`).join(' '));
</script>

{#if groups.length === 0}
  <div class="bars-empty">Nothing to compare in this period.</div>
{:else}
  <div class="bars">
    <svg viewBox="0 0 {WIDTH} {HEIGHT}" role="img" aria-label={caption}>
      {#each axisTicks as tick (tick)}
        {@const y = yOf(tick)}
        <line
          class="gridline"
          class:gridline--zero={tick === 0}
          x1={marginLeft}
          x2={marginLeft + PLOT_WIDTH}
          y1={y}
          y2={y}
        />
        {#if tickLabel}
          <text x={marginLeft - 8} y={y + 4} text-anchor="end" class="bars__tick">
            {tickLabel(tick)}
          </text>
        {/if}
      {/each}

      {#each placed as p (p.group.key)}
        {#each p.bars as bar (bar.series)}
          <rect
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            fill="var(--chart-{bar.colorIndex + 1})"
            rx="2"
          >
            <title>{p.group.label} · {bar.display}</title>
          </rect>
        {/each}
        <text x={p.labelX} y={MARGIN.top + PLOT_HEIGHT + 18} text-anchor="middle" class="bars__label">
          {p.group.label}
        </text>
      {/each}

      {#if hasLine && linePoints.length > 0}
        {#if linePoints.length > 1}
          <polyline class="bars__line" points={linePath} />
        {/if}
        {#each linePoints as point, i (i)}
          <circle class="bars__dot" cx={point.x} cy={point.y} r="3.5" />
        {/each}
      {/if}
    </svg>

    <table class="visually-hidden">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Group</th>
          {#each series as se (se.key)}
            <th scope="col">{se.label}</th>
          {/each}
          {#if hasLine}<th scope="col">{lineLabel}</th>{/if}
        </tr>
      </thead>
      <tbody>
        {#each groups as group (group.key)}
          <tr>
            <th scope="row">{group.label}</th>
            {#each series as se (se.key)}
              {@const bar = group.bars.find((b) => b.series === se.key)}
              <td>{bar ? bar.display : '—'}</td>
            {/each}
            {#if hasLine}
              <td>{group.lineDisplay ?? '—'}</td>
            {/if}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  .bars {
    display: grid;
    gap: 0.4rem;
  }

  svg {
    width: 100%;
    height: auto;
  }

  .gridline {
    stroke: var(--line-soft);
    stroke-width: 1;
  }
  .gridline--zero {
    stroke: var(--line);
  }

  .bars__label {
    font-size: 9px;
    fill: var(--ink-faint);
    font-weight: 600;
  }

  /*
   * A shade lighter than the group labels under the plot: the axis is there to
   * be measured against, not read first.
   */
  .bars__tick {
    font-size: 8.5px;
    fill: var(--ink-faint);
    font-variant-numeric: tabular-nums;
  }

  .bars__line {
    fill: none;
    stroke: var(--brand-dark);
    stroke-width: 2;
    stroke-linejoin: round;
    stroke-linecap: round;
  }
  .bars__dot {
    fill: var(--paper);
    stroke: var(--brand-dark);
    stroke-width: 2;
  }

  .bars-empty {
    display: grid;
    place-items: center;
    min-height: 10rem;
    padding: 1.2rem;
    text-align: center;
    color: var(--ink-soft);
    background: var(--bg-sunken);
    border-radius: var(--radius-lg);
  }
</style>
